<script setup>
import { onMounted, watch, computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { initFlowbite } from 'flowbite'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from './stores/UserStore'
import { useListingsStore } from '@/stores/ListingsStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useHomeStore } from '@/stores/HomeStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import { usePusher } from '@/hooks/usePusher'
import { useCello } from '@/hooks/useCello'
import { useStrapiStore } from '@/stores/StrapiStore'
import MainLayout from '@/components/MainLayout/index.vue'
import GlobalVideoWidget from '@/components/GlobalVideoWidget.vue'
import NewVersionToast from '@/components/NewVersionToast.vue'
import ListingsExportToast from '@/components/ListingsExportToast.vue'
import MaintenancePage from '@/views/MaintenancePage.vue'
import { setErrorHandlerUser } from '@/services/errorHandler'
import ToastNotification from '@/components/ToastNotification.vue'
import UpgradeFeatureModal from '@/components/UpgradePlans/UpgradeFeatureModal.vue'
import UpgradeWindow from '@/components/UpgradePlans/UpgradeWindow.vue'
import UpgradePlanSuccessModal from '@/components/UpgradePlans/UpgradePlanSuccessModal.vue'
import { POSTHOG_EVENTS } from '@/constants/posthog'
import { useNewPlans } from '@/helpers/featureFlags'

const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE

const posthog = inject('posthog')

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const listingsStore = useListingsStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const connectionStore = useConnectionStore()
const homeStore = useHomeStore()
const strapiStore = useStrapiStore()
const isAuthLayout = computed(() => !route.meta?.requireAuth)
const user = computed(() => userStore.user)

const isNewPlans = useNewPlans()

useKeyboardShortcut(router)
usePusher(userStore)
useCello()

globalStore.fetchBuildTxt()
strapiStore.fetchSystemStatusNotificationBar()

setInterval(() => {
  globalStore.fetchBuildTxt()
}, 300000)

onMounted(async () => {
  globalStore.windowWidth = window.innerWidth
  globalStore.windowHeight = window.innerHeight
  window.addEventListener('resize', () => {
    globalStore.windowWidth = window.innerWidth
    globalStore.windowHeight = window.innerHeight

    if (window.innerWidth < 1280) {
      globalStore.sidebar = false
    }
  })

  // initialize components based on data attribute selectors
  initFlowbite()

  const params = new URLSearchParams(window.location.search)
  const authAs = params.get('authAs')
  const sessionOverrideAuthToken = sessionStorage.getItem('cdp_auth_override_token')

  if (authAs || sessionOverrideAuthToken) {
    await userStore.checkSession(authAs || sessionOverrideAuthToken)
    console.log('logged in as', userStore.user?.email)

    if (route.query?.authAs) {
      router.replace('/inventory')
    } else if (route.query?.backTo) {
      router.replace(route.query.backTo)
    }
    
  } else {
    userStore.checkSession()
  }

  if (navigator.userAgent.search('Mac') !== -1) {
    globalStore.userAgent = 'cmd'
  } else {
    globalStore.userAgent = 'ctrl'
  }
  globalStore.sidebar =  JSON.parse(localStorage.getItem('sidebar') || false)
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
})

watch(user, () => {
  userStore.user && !configStore.ignoreConfigUpdate && configStore.fetchConfigs({ withoutLoader: true })
  userStore.user && setErrorHandlerUser(userStore.user)

  configStore.ignoreConfigUpdate = false

  if (!userStore.flags[`ebay_listings_corrected`]) {
    listingsStore.correctListings('ebay')
  }
})

watch(() => connectionStore.addedChannels.length, (newLen, oldLen) => {
  if (newLen === 1 && oldLen === 0) {
    homeStore.updateCaptureAction('added-connection')

    posthog.capture(POSTHOG_EVENTS.CONNECTION_ADDED, { 
      userId: userStore.user.id, 
      connection_type: connectionStore.addedChannels[0].name,
      timestamp: new Date().toISOString(),
    })
  }
})

// NEW_PLANS_FLAG: redirect non-beta users to settings/plans when upgrade is triggered
watch(() => billingStore.upgradeFeatureModal, (val) => {
  if (val && !isNewPlans.value) {
    billingStore.upgradeFeatureModal = false
    billingStore.upgradeFeatureModalData = {}
    router.push('/settings/plans')
  }
})

watch(() => billingStore.upgradeWindow, (val) => {
  if (val && !isNewPlans.value) {
    billingStore.upgradeWindow = false
    router.push('/settings/plans')
  }
})

const safeZE = (...args) => {
  try {
    if (typeof zE !== 'undefined') zE(...args)
  } catch (e) {}
}

const setZendeskPosition = (bottom, right) => {
  let style = document.getElementById('ze-position-override')
  if (!style) {
    style = document.createElement('style')
    style.id = 'ze-position-override'
    document.head.appendChild(style)
  }
  style.textContent = `
    #launcher { bottom: ${bottom} !important; right: ${right} !important; }
    iframe[title*="Button to launch"] { bottom: ${bottom} !important; right: ${right} !important; }
  `
}

const updateZendeskPosition = () => {
  if (['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted', 'SignIn', 'SignUp', 'SignUpEmail', 'ResetPassword', 'SetPassword', 'EmailSent', 'LinkExpired'].includes(route.name)) {
    safeZE('messenger', 'hide')
  } else if (['/batches', '/inventory'].includes(route.path)) {
    safeZE('messenger', 'show')
    setZendeskPosition('55px', globalStore.videoWidgetOpen ? '380px' : '75px')
  } else if (route.path.includes('history')) {
    safeZE('messenger', 'show')
    setZendeskPosition('50px', globalStore.videoWidgetOpen ? '380px' : '26px')
  } else {
    safeZE('messenger', 'show')
    setZendeskPosition('12px', globalStore.videoWidgetOpen ? '380px' : '26px')
  }
}

watch(route, updateZendeskPosition, { immediate: true })
watch(() => globalStore.videoWidgetOpen, updateZendeskPosition)

// Global error handler
window.onerror = function (message, source, lineno, colno, error) {
  if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message,
      source,
      lineno,
      colno,
      error
    })
  }

  if (error instanceof RangeError) {
    console.error('Global RangeError Caught:', error)
  }

  // Return false to prevent the default handling of the error
  return false
};

// Event listener for global errors
window.addEventListener('error', function (event) {
  if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  }
});

// Event listener for unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected (unhandled rejection):', event.reason);
  }
});
</script>

<template>
  <!-- <RouterView v-if="isAuthLayout || configStore.config?.subscription?.plan_id" /> -->
  <MaintenancePage v-if="isMaintenanceMode === 'true' || globalStore.maintenanceMode" />
  <template v-else>
    <NewVersionToast />
    <ListingsExportToast />
    <ToastNotification />

    <RouterView v-if="isAuthLayout || ['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted'].includes(route.name)" />
    <template v-else>
      <MainLayout />
      <GlobalVideoWidget />

      <!-- NEW_PLANS_FLAG: new plans modals only for beta testers -->
      <template v-if="isNewPlans">
        <UpgradeFeatureModal />
        <UpgradeWindow v-if="billingStore.upgradeWindow" @close="billingStore.upgradeWindow = false" />
        <UpgradePlanSuccessModal v-if="billingStore.upgradePlanSuccessModal" @close="billingStore.upgradePlanSuccessModal = false" />
      </template>
    </template>
  </template>
</template>